import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useSEO from "@/hooks/useSEO";

const serviceItems = [
  {
    num: "01",
    icon: "ri-bank-card-line",
    titleKey: "service.s1.title",
    descKey: "service.s1.desc",
    color: "cyan",
    imgUrl: "https://readdy.ai/api/search-image?query=bank-transfer-payment-incoming-funds-digital-banking-interface-glowing-cyan-data-streams-dark-background-financial-transaction-visualization-abstract-no-people-clean-minimal-fintech&width=800&height=500&seq=svc_payment_tracking_01&orientation=landscape",
  },
  {
    num: "02",
    icon: "ri-exchange-line",
    titleKey: "service.s2.title",
    descKey: "service.s2.desc",
    color: "emerald",
    imgUrl: "https://readdy.ai/api/search-image?query=data-reconciliation-matching-process-visualization-green-glowing-checkmarks-spreadsheet-automation-abstract-dark-background-financial-data-comparison-no-people-modern-fintech&width=800&height=500&seq=svc_reconciliation_02&orientation=landscape",
  },
  {
    num: "03",
    icon: "ri-money-dollar-circle-line",
    titleKey: "service.s3.title",
    descKey: "service.s3.desc",
    color: "violet",
    imgUrl: "https://readdy.ai/api/search-image?query=fund-management-cash-flow-settlement-abstract-visualization-purple-violet-glowing-nodes-financial-network-dark-background-money-circulation-concept-no-people-clean&width=800&height=500&seq=svc_fund_settlement_03&orientation=landscape",
  },
  {
    num: "04",
    icon: "ri-bar-chart-grouped-line",
    titleKey: "service.s4.title",
    descKey: "service.s4.desc",
    color: "amber",
    imgUrl: "https://readdy.ai/api/search-image?query=financial-reporting-analytics-dashboard-amber-orange-charts-graphs-data-visualization-business-intelligence-dark-background-KPI-metrics-abstract-no-people-professional&width=800&height=500&seq=svc_reporting_04&orientation=landscape",
  },
  {
    num: "05",
    icon: "ri-robot-line",
    titleKey: "service.s5.title",
    descKey: "service.s5.desc",
    color: "rose",
    imgUrl: "https://readdy.ai/api/search-image?query=AI-automation-system-robotic-process-automation-accounting-software-rose-pink-glowing-circuit-patterns-machine-learning-financial-automation-dark-background-no-people-futuristic&width=800&height=500&seq=svc_automation_05&orientation=landscape",
  },
];

const flowSteps = [
  { step: "01", icon: "ri-chat-3-line", label: "ヒアリング", labelEn: "Consultation" },
  { step: "02", icon: "ri-file-list-3-line", label: "提案・設計", labelEn: "Proposal" },
  { step: "03", icon: "ri-settings-3-line", label: "システム構築", labelEn: "Setup" },
  { step: "04", icon: "ri-test-tube-line", label: "テスト・検証", labelEn: "Testing" },
  { step: "05", icon: "ri-rocket-line", label: "運用開始", labelEn: "Go Live" },
];

const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

const ServicePage = () => {
  const { t } = useTranslation();

  useSEO({
    title: "サービス内容 | 決済管理・資金管理・自動化システム",
    description: "Stream Solutions Limitedのサービス内容。入金情報管理・振込データ照合（消込処理）・資金精算支援・レポーティング・決済経理業務の自動化システム開発を提供します。",
    keywords: "決済管理サービス,消込処理,入金管理,資金精算,自動化システム,経理業務効率化",
    canonical: "/service",
    ogTitle: "サービス内容 | Stream Solutions Limited",
    ogDescription: "入金管理から消込処理・自動化システムまで、決済管理業務をワンストップで支援します。",
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract-financial-technology-dark-background-with-flowing-data-streams-network-connections-glowing-lines-deep-navy-blue-atmosphere-digital-payment-system-visualization-no-people-clean-minimal-futuristic&width=1920&height=600&seq=svchero001&orientation=landscape"
            alt="service hero"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 to-slate-950" />
        </div>
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(6,182,212,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.3) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-px bg-cyan-500" />
            <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">SERVICES</span>
            <span className="w-8 h-px bg-cyan-500" />
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">{t("service.hero.title")}</h1>
          <p className="text-slate-400 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            {t("service.hero.sub")}
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="space-y-20">
            {serviceItems.map((svc, i) => (
              <AnimatedSection key={svc.num} delay={0.1}>
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    i % 2 === 1 ? "lg:flex-row-reverse" : ""
                  }`}
                >
                  {/* Image side */}
                  <div className={`${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <div
                      className="relative overflow-hidden aspect-[16/10]"
                      style={{
                        borderRadius: "1.5rem",
                        clipPath: i % 2 === 0
                          ? "polygon(0 0, 100% 0, 95% 100%, 0% 100%)"
                          : "polygon(5% 0, 100% 0, 100% 100%, 0% 100%)",
                      }}
                    >
                      <img
                        src={svc.imgUrl}
                        alt={t(svc.titleKey)}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/30 to-transparent" />
                      <div className="absolute top-5 left-5 text-6xl font-black text-white/10 select-none">
                        {svc.num}
                      </div>
                    </div>
                  </div>

                  {/* Text side */}
                  <div className={`${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-cyan-50">
                        <i className={`${svc.icon} text-cyan-600 text-lg`} />
                      </div>
                      <span className="text-cyan-600 text-xs font-semibold tracking-[0.2em] uppercase">
                        Service {svc.num}
                      </span>
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-5 leading-tight">
                      {t(svc.titleKey)}
                    </h2>
                    <p className="text-slate-600 text-base leading-relaxed mb-6">
                      {t(svc.descKey)}
                    </p>
                    <div className="h-px bg-gradient-to-r from-cyan-200 to-transparent" />
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Flow section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="w-8 h-px bg-cyan-500" />
                <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">PROCESS</span>
                <span className="w-8 h-px bg-cyan-500" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t("service.flow.title")}</h2>
              <p className="text-slate-400 text-base max-w-xl mx-auto">{t("service.flow.sub")}</p>
            </div>
          </AnimatedSection>

          <div className="relative">
            {/* Connector line */}
            <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />

            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6">
              {flowSteps.map((step, i) => (
                <AnimatedSection key={step.step} delay={i * 0.1}>
                  <div className="flex flex-col items-center text-center">
                    <div className="relative w-20 h-20 flex items-center justify-center rounded-full border border-cyan-500/30 bg-slate-900 mb-4">
                      <i className={`${step.icon} text-cyan-400 text-2xl`} />
                      <div className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-cyan-500 text-white text-xs font-bold">
                        {step.step}
                      </div>
                    </div>
                    <div className="text-white font-semibold text-sm mb-1">{step.label}</div>
                    <div className="text-slate-500 text-xs">{step.labelEn}</div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
              {t("top.cta.title")}
            </h2>
            <p className="text-slate-600 mb-8">{t("top.cta.sub")}</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {t("top.cta.btn")}
              <i className="ri-arrow-right-line" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;
